/**
 * Pick an ACP permission RPC result that approves the request (for CLI "dangerous" mode).
 * Prefers a single-step allow (`allow_once`, or any non–`allow_always` option) over `allow_always`.
 */

type NormalizedOption = { optionId: string; kind?: string };

function isRejectKind(kind?: string): boolean {
  return kind === 'reject_once' || kind === 'reject_always';
}

function normalizeOptions(raw: unknown): NormalizedOption[] {
  if (!Array.isArray(raw)) return [];
  const out: NormalizedOption[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object' || Array.isArray(item)) continue;
    const o = item as Record<string, unknown>;
    const rawId = o.optionId ?? o.id;
    const optionId =
      typeof rawId === 'string' && rawId.trim() !== ''
        ? rawId.trim()
        : typeof rawId === 'number' && Number.isFinite(rawId)
          ? String(rawId)
          : '';
    if (!optionId) continue;
    const kind = typeof o.kind === 'string' ? o.kind : undefined;
    out.push({ optionId, ...(kind ? { kind } : {}) });
  }
  return out;
}

function pickAllowOption(options: NormalizedOption[]): NormalizedOption | null {
  const nonReject = options.filter((o) => !isRejectKind(o.kind));
  if (nonReject.length === 0) return null;

  const allowOnce = nonReject.find((o) => o.kind === 'allow_once');
  if (allowOnce) return allowOnce;

  const notAlways = nonReject.filter((o) => o.kind !== 'allow_always');
  if (notAlways.length > 0) return notAlways[0] ?? null;

  return nonReject.find((o) => o.kind === 'allow_always') ?? nonReject[0] ?? null;
}

function firstNonEmptyOptionsArray(...candidates: unknown[]): unknown[] {
  for (const c of candidates) {
    if (Array.isArray(c) && c.length > 0) return c;
  }
  const fallback = candidates[0];
  return Array.isArray(fallback) ? fallback : [];
}

/**
 * Option rows for `session/request_permission` across ACP agents (top-level `options` vs nested shapes).
 */
export function extractAcpPermissionRequestOptionArray(params: Record<string, unknown>): unknown[] {
  const toolCall = params.toolCall;
  const fromToolCall =
    toolCall != null && typeof toolCall === 'object' && !Array.isArray(toolCall)
      ? (toolCall as Record<string, unknown>)
      : null;
  return firstNonEmptyOptionsArray(
    params.options,
    params.permissionOptions,
    fromToolCall?.options,
    fromToolCall?.permissionOptions,
  );
}

/**
 * RPC `result` for `session/request_permission` when auto-approving (prefer one-shot allow, not “always”).
 * Returns `null` when there is no allow-like option to select.
 */
export function buildCliAutoApprovedPermissionRpcResult(
  requestParams: Record<string, unknown>,
): { outcome: { outcome: 'selected'; optionId: string; _meta?: { permissionOptionKind: string } } } | null {
  const opt = pickAllowOption(normalizeOptions(extractAcpPermissionRequestOptionArray(requestParams)));
  if (!opt) return null;
  const kind = opt.kind?.trim();
  return {
    outcome: {
      outcome: 'selected',
      optionId: opt.optionId,
      ...(kind ? { _meta: { permissionOptionKind: kind } } : {}),
    },
  };
}
